// Loads the Maps JS bootstrap so the Places UI Kit custom elements and the
// interactive Map constructor exist.
//
// This is the ONLY place Google is contacted from the browser, and it happens lazily:
// nothing loads until a result card is expanded or the map renders. If no key is
// configured the loader is never called and the app works exactly as before, minus
// ratings and map.
//
// Uses Google's Dynamic Library Import pattern: a small inline snippet sets up
// `importLibrary` as a queueing proxy BEFORE the bootstrap script loads. This avoids
// the race condition where `script.onload` fires before the bootstrap has finished
// setting up `importLibrary` internally (which happens with `loading=async`).

let bootstrapping: Promise<void> | null = null;

declare global {
  interface Window {
    google?: { maps?: { importLibrary?: (name: string) => Promise<unknown> } };
  }
}

/** Sets up the Dynamic Library Import inline and resolves when the core JS is ready. */
function ensureBootstrap(apiKey: string): Promise<void> {
  if (bootstrapping) return bootstrapping;

  bootstrapping = new Promise<void>((resolve) => {
    if (window.google?.maps?.importLibrary) {
      resolve();
      return;
    }

    // Google's recommended Dynamic Library Import bootstrap. This inline snippet
    // creates `google.maps.importLibrary` as a proxy that queues calls until the
    // real library arrives. The proxy is available synchronously - no race condition.
    /* eslint-disable */
    ((g: Record<string, string>) => {
      let h: Promise<void> | undefined;
      let a: HTMLScriptElement;
      let k: string;
      const p = "The Google Maps JavaScript API";
      const c = "google";
      const l = "importLibrary";
      const q = "__ib__";
      const m = document;
      const b: any = window;
      b[c] = b[c] || {};
      const d = b[c].maps || (b[c].maps = {});
      const r = new Set<string>();
      const e = new URLSearchParams();
      const u = () =>
        h ||
        (h = new Promise<void>(async (f, n) => {
          a = m.createElement("script");
          e.set("libraries", [...r] + "");
          for (k in g)
            e.set(
              k.replace(/[A-Z]/g, (t: string) => "_" + t.charAt(0).toLowerCase()),
              g[k] as string,
            );
          e.set("callback", c + ".maps." + q);
          a.src = `https://maps.googleapis.com/maps/api/js?` + e;
          d[q] = f;
          a.onerror = () => {
            h = undefined;
            n(Error(p + " could not load."));
          };
          const nonceEl = m.querySelector<HTMLScriptElement>("script[nonce]");
          a.nonce = nonceEl?.nonce ?? "";
          m.head.append(a);
        }));
      d[l]
        ? console.warn(p + " only loads once. Ignoring:", g)
        : (d[l] = (f: string, ...n: unknown[]) =>
            r.add(f) && u().then(() => d[l](f, ...n)));
    })({
      key: apiKey,
      v: "weekly",
    });
    /* eslint-enable */

    // importLibrary is now a synchronous proxy. Resolve immediately - the actual
    // library load happens when importLibrary is first called.
    resolve();
  });

  return bootstrapping;
}

export async function loadPlacesUiKit(apiKey: string): Promise<void> {
  await ensureBootstrap(apiKey);
  await window.google!.maps!.importLibrary!("places");
}

export async function loadMapsLibrary(apiKey: string): Promise<void> {
  await ensureBootstrap(apiKey);
  await window.google!.maps!.importLibrary!("maps");
}

/** Stored promise so geocodeAddress can await it even if called before it resolves. */
let geocodingReady: Promise<void> | null = null;

/** Eagerly load geocoding so it's ready by the time the user submits a free-text address. */
export function loadGeocodingLibrary(apiKey: string): Promise<void> {
  if (!geocodingReady) {
    geocodingReady = ensureBootstrap(apiKey).then(() =>
      window.google!.maps!.importLibrary!("geocoding") as Promise<void>,
    );
  }
  return geocodingReady;
}

/** Geocode free text via the Maps JS Geocoder.
 *  WAITS for the geocoding library to load rather than silently returning null. */
export async function geocodeAddress(
  address: string,
  near?: { lat: number; lng: number },
): Promise<{ lat: number; lng: number } | null> {
  // Wait for the geocoding library — it was started eagerly at app boot.
  // If it hasn't finished yet, this blocks until it does.
  if (geocodingReady) {
    try {
      await geocodingReady;
    } catch {
      return null;
    }
  }
  if (!window.google?.maps?.Geocoder) return null;
  try {
    const geo = new google.maps.Geocoder();
    // Bias towards the group's city when possible
    const suffix = near ? ", Bengaluru, India" : ", India";
    const result = await geo.geocode({ address: address + suffix });
    if (result.results[0]) {
      const loc = result.results[0].geometry.location;
      return { lat: loc.lat(), lng: loc.lng() };
    }
  } catch {
    // geocode failed
  }
  return null;
}
